import {
  Button,
  TableCell,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@folio/components/ui';
import { DocumentDownloadButton } from '@folio/components/module-elements/Documents/DocumentDownloadButton';
import { invoiceDocumentsOf } from './invoiceDocuments';
import type { CustomerDtoPaymentModeEnumKey, Invoice } from 'korint-sdk';
import type { InvoiceDtoStatusEnumKey } from 'korint-sdk';
import {
  ArrowLeftRightIcon,
  ChevronLeftIcon,
  EyeIcon,
  HandCoinsIcon,
  IterationCwIcon,
} from 'lucide-react';
import { cva } from 'class-variance-authority';
import { formatDate, formatPrice } from '@folio/lib';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { KorintSuperLink } from '@folio/components/super-link';
import { RetryChargeOnInvoice } from './RetryChargeOnInvoice';
import { ManuallyMarkInvoiceDialog } from './ManuallyMarkInvoiceDialog';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export type InvoiceGroup = {
  displayInvoice: Invoice;
  originalInvoice?: Invoice;
  creditInvoice?: Invoice;
};

interface InvoiceGroupRowProps {
  group: InvoiceGroup;
  customerId: string;
  customerPaymentMode?: CustomerDtoPaymentModeEnumKey;
  offlineEnabled?: boolean;
  nextRetryAt?: string;
  onOpenDetails: (group: InvoiceGroup) => void;
  onRetrySuccess?: () => void;
  onViewInvoice?: (invoice: Invoice) => void;
}

type InvoiceStatus = InvoiceDtoStatusEnumKey | 'CREDIT_FINALIZED';

const statusBadgeVariants = cva('w-fit px-2 py-1 rounded-md font-semibold text-center text-xs', {
  variants: {
    status: {
      DRAFTED: 'bg-gray-100 text-gray-700',
      ISSUED: 'bg-blue-100 text-blue-700',
      PAID: 'bg-green-100 text-green-700',
      FINALIZED: 'bg-purple-100 text-purple-700',
      CREDIT_FINALIZED: 'bg-orange-100 text-orange-700',
      PAYMENT_IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
      PAYMENT_FAILED: 'bg-red-100 text-red-700',
      RECONCILED: 'bg-orange-100 text-orange-700',
      WRITTEN_OFF: 'bg-orange-100 text-orange-700',
      CANCELLED: 'bg-orange-100 text-orange-700',
      PAYMENT_DISPUTED: 'bg-red-100 text-red-700',
      REFUND_NOT_HANDLED: 'bg-red-100 text-red-700',
      REFUND_IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
      PARTIALLY_FINALIZED: 'bg-red-100 text-red-700',
    },
  },
});

const adjustedInvoicesStatus = (invoice: Invoice): InvoiceStatus => {
  if (invoice.creditedInvoiceId && invoice.status === 'FINALIZED') {
    return 'CREDIT_FINALIZED';
  }
  return invoice.status as InvoiceStatus;
};

export function InvoiceGroupRow({
  group,
  customerId,
  customerPaymentMode,
  offlineEnabled,
  nextRetryAt,
  onOpenDetails,
  onRetrySuccess,
  onViewInvoice,
}: InvoiceGroupRowProps) {
  const { t, i18n } = useTranslation();
  const invoice = group.displayInvoice;
  const documents = invoiceDocumentsOf(invoice, t);
  const isReplacement = Boolean(group.originalInvoice);

  const { policy } = usePolicyOnDashboard();
  const { can } = useUserPermissions({
    productId: policy.productId,
    actions: ['RETRY_CHARGE', 'MANUALLY_MARK_INVOICE'],
  });

  // retryCharge is rejected for offline customers: chargeCustomer would never collect
  const showRetryButton =
    (invoice.status === 'PAYMENT_FAILED' ||
      invoice.status === 'PAYMENT_DISPUTED' ||
      (invoice.status === 'ISSUED' && invoice.total.amountWithFeesAndTaxes > 0)) &&
    can.RETRY_CHARGE &&
    customerPaymentMode !== 'OFFLINE';

  // The dialog resolves the invoice's own eligibility and renders nothing when it has none
  const canManuallyMark = can.MANUALLY_MARK_INVOICE && Boolean(offlineEnabled);

  return (
    <TableRow key={invoice.id}>
      <TableCell>
        <Group gap={2}>
          <KorintSuperLink subpath={`/invoices/${invoice.id}`} />
          <div className="w-4 flex-shrink-0">
            {isReplacement && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <ArrowLeftRightIcon className="h-4 w-4 text-blue-600 cursor-default" />
                </TooltipTrigger>
                <TooltipContent>{t('invoices.module.replacementTooltip')}</TooltipContent>
              </Tooltip>
            )}
          </div>
          {formatDate(new Date(invoice.createdAt))}
        </Group>
      </TableCell>
      <TableCell>
        {formatDate(new Date(invoice.period.startedAt))} →{' '}
        {formatDate(new Date(invoice.period.endedAt))}
      </TableCell>
      <TableCell>
        <Group gap={2}>
          <div
            className={statusBadgeVariants({
              status: adjustedInvoicesStatus(invoice),
            })}
          >
            {t(`invoices.status.${adjustedInvoicesStatus(invoice)}`)}
          </div>
          {invoice.manualPayment && (
            <Tooltip>
              <TooltipTrigger asChild>
                <HandCoinsIcon className="h-4 w-4 text-blue-600 cursor-default" />
              </TooltipTrigger>
              <TooltipContent>
                {t('invoices.module.manualPaymentTooltip', {
                  method: t(`invoices.manualMark.method.${invoice.manualPayment.method}`),
                  date: formatDate(new Date(invoice.manualPayment.appliedAt)),
                })}
              </TooltipContent>
            </Tooltip>
          )}
        </Group>
        {nextRetryAt && (
          <Text size="xs" tone="muted" className="mt-1 flex items-center gap-1">
            <IterationCwIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
            {t('invoices.recovery.scheduledRetry', {
              date: new Intl.DateTimeFormat(i18n.language, {
                day: '2-digit',
                month: '2-digit',
                timeZone: 'Europe/Paris',
              }).format(new Date(nextRetryAt)),
            })}
          </Text>
        )}
      </TableCell>
      <TableCell>{formatPrice(invoice.total.amountWithFeesAndTaxes)}</TableCell>
      <TableCell className="text-right">
        <Group justify="end" gap={1}>
          {canManuallyMark && (
            <ManuallyMarkInvoiceDialog
              customerId={customerId}
              invoice={invoice}
              handleSuccess={onRetrySuccess}
            />
          )}

          {documents.length === 0 ? (
            // Keeps the row's action icons aligned with rows that have a document
            <div className="invisible">
              <DocumentDownloadButton document={{ metadata: {} }} showButtonText={false} />
            </div>
          ) : (
            <Stack gap={1}>
              {documents.map(({ id, signedUrl, fileName, label }) => (
                <DocumentDownloadButton
                  key={id}
                  document={{ id, signedUrl, metadata: { scanresult: 'CLEAN' }, name: fileName }}
                  showButtonText={false}
                >
                  {label}
                </DocumentDownloadButton>
              ))}
            </Stack>
          )}

          <div className={showRetryButton ? undefined : 'invisible'}>
            <Tooltip>
              <RetryChargeOnInvoice
                customerId={customerId}
                invoiceId={invoice.id}
                handleSuccess={onRetrySuccess}
                trigger={
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('invoices.module.retryPayment')}
                      className="h-8 w-8 text-foreground hover:bg-slate-100"
                    >
                      <IterationCwIcon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                }
              />
              <TooltipContent>{t('invoices.module.retryPayment')}</TooltipContent>
            </Tooltip>
          </div>

          <div className={documents.length && onViewInvoice ? undefined : 'invisible'}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={e => {
                    e.stopPropagation();
                    if (documents.length) onViewInvoice?.(invoice);
                  }}
                  variant="ghost"
                  size="icon"
                  aria-label={t('invoices.module.view')}
                  className="h-8 w-8 text-foreground hover:bg-slate-100"
                >
                  <EyeIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('invoices.module.view')}</TooltipContent>
            </Tooltip>
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={e => {
                  e.stopPropagation();
                  onOpenDetails(group);
                }}
                variant="ghost"
                size="icon"
                aria-label={t('invoices.module.viewDetails')}
                className="h-8 w-8 text-foreground hover:bg-slate-100"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('invoices.module.viewDetails')}</TooltipContent>
          </Tooltip>
        </Group>
      </TableCell>
    </TableRow>
  );
}
